import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');  // Используем правильное имя ключа
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // When responseType is 'blob', error responses arrive as Blob too.
    // Parse them back to JSON so the handlers below can read the data.
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        error.response.data = JSON.parse(text);
      } catch {
        // Not JSON — leave as Blob
      }
    }

    if (error.response?.status === 401) {
      // Only redirect on auth-related 401s, not on downstream service errors
      // or fire-and-forget calls like logout
      const url = error.config?.url || '';
      const isStripeEndpoint = url.includes('/stripe/');
      const isLogoutEndpoint = url.includes('/auth/logout');
      const isLogoEndpoint = url.includes('/auth/gestoria/logo-image');
      if (!isStripeEndpoint && !isLogoutEndpoint && !isLogoEndpoint) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
    }

    // Handle account blocked 403
    if (error.response?.status === 403 && error.response?.data?.accountBlocked) {
      window.location.href = '/account-blocked';
    }

    // Handle subscription expired 403 — redirect to subscription page
    if (error.response?.status === 403 && error.response?.data?.subscriptionExpired) {
      window.location.href = '/subscription';
    }

    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  requestMagicLink: (email: string) =>
    api.post('/auth/magic-link', { email }),
  
  verifyMagicLink: (token: string) =>
    api.get<{ accessToken: string; user: User }>(`/auth/verify?token=${token}`),
  
  getMe: () =>
    api.get<User>('/auth/me'),
  
  updateNotificationPreferences: (preferences: {
    emailNotifications?: boolean;
    deadlineReminders?: boolean;
    documentUpdates?: boolean;
  }) =>
    api.patch<{ emailNotifications: boolean; deadlineReminders: boolean; documentUpdates: boolean }>(
      '/auth/notification-preferences',
      preferences
    ),

  updateGestoria: (data: { name: string }) =>
    api.patch<{ id: string; name: string }>('/auth/gestoria', data),

  uploadLogo: (data: FormData) =>
    api.post<{ id: string; logoUrl: string | null }>('/auth/gestoria/logo', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  removeLogo: () =>
    api.delete<{ id: string; logoUrl: null }>('/auth/gestoria/logo'),

  getLogoImage: () =>
    api.get<Blob>('/auth/gestoria/logo-image', {
      responseType: 'blob',
      params: { _t: Date.now() }, // cache-busting: ensure fresh image after upload
    }),

  updateProfile: (data: { name: string }) =>
    api.patch<{ id: string; name: string }>('/auth/profile', data),

  acceptGdprConsent: () =>
    api.patch<{ id: string; gdprConsentAt: string }>('/auth/gdpr-consent'),

  logout: () =>
    api.post('/auth/logout'),

  blockAccount: () =>
    api.post<{ message: string; blockedAt: string; exportDeadline: string }>('/auth/block-account'),

  exportData: () =>
    api.get('/auth/export-data'),

  getIntegrations: () =>
    api.get<{
      holdedConfigured: boolean;
      holdedApiKeyMasked: string | null;
      quipuConfigured: boolean;
      quipuAppIdMasked: string | null;
      sageConfigured: boolean;
      sageBusinessId: string | null;
      a3innuvaConfigured: boolean;
      a3innuvaCompanyId: string | null;
      facturadirectaConfigured: boolean;
      facturadirectaApiKeyMasked: string | null;
      facturadirectaCompanyId: string | null;
    }>(
      '/auth/integrations',
    ),

  updateIntegrations: (data: {
    holdedApiKey?: string;
    quipuAppId?: string;
    quipuAppSecret?: string;
    sageDisconnect?: boolean;
    a3innuvaDisconnect?: boolean;
    facturadirectaApiKey?: string;
    facturadirectaCompanyId?: string;
  }) =>
    api.patch<{
      holdedConfigured: boolean;
      holdedApiKeyMasked: string | null;
      quipuConfigured: boolean;
      quipuAppIdMasked: string | null;
      sageConfigured: boolean;
      sageBusinessId: string | null;
      a3innuvaConfigured: boolean;
      a3innuvaCompanyId: string | null;
      facturadirectaConfigured: boolean;
      facturadirectaApiKeyMasked: string | null;
      facturadirectaCompanyId: string | null;
    }>(
      '/auth/integrations',
      data,
    ),

  getSageConnectUrl: () =>
    api.get<{ url: string }>('/sage/connect'),

  getA3innuvaConnectUrl: () =>
    api.get<{ url: string }>('/a3innuva/connect'),
};

// Clients
export const clientsApi = {
  list: (params?: ClientsQueryParams) =>
    api.get<PaginatedClientsResponse>('/clients', { params }),
  
  get: (id: string) =>
    api.get<Client>(`/clients/${id}`),
  
  create: (data: CreateClientDto) =>
    api.post<Client>('/clients', data),
  
  update: (id: string, data: UpdateClientDto) =>
    api.patch<Client>(`/clients/${id}`, data),
  
  delete: (id: string) =>
    api.delete(`/clients/${id}`),
  
  getUploadUrl: (id: string) =>
    api.get<{ uploadUrl: string; uploadToken: string }>(`/clients/${id}/upload-url`),

  updateCaseStatus: (id: string, data: { status: string; note?: string }) =>
    api.patch<{ id: string; caseStatus: string; caseStatusAt: string }>(`/clients/${id}/case-status`, data),

  getCaseStatusHistory: (id: string, params?: { limit?: number; offset?: number }) =>
    api.get<{ data: CaseStatusHistoryRecord[]; meta: { total: number; limit: number; offset: number } }>(`/clients/${id}/case-status/history`, { params }),

  exportCsv: (params?: ClientsQueryParams) =>
    api.get('/clients/export', { params, responseType: 'blob' }),
};

// Documents
export const documentsApi = {
  list: (filters?: DocumentFilters) =>
    api.get<Document[]>('/documents', { params: filters }),
  
  get: (id: string) =>
    api.get<Document>(`/documents/${id}`),
  
  upload: (data: FormData) =>
    api.post<Document>('/documents/upload', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  
  update: (id: string, data: UpdateDocumentDto) =>
    api.patch<Document>(`/documents/${id}`, data),
  
  delete: (id: string) =>
    api.delete(`/documents/${id}`),
  
  getDownloadUrl: (id: string) =>
    api.get<{ url: string; fileName: string }>(`/documents/${id}/download`),

  download: (id: string) =>
    api.get(`/documents/${id}/download`, { responseType: 'blob' }),

  getExpiring: (days?: number) =>
    api.get<Document[]>('/documents/expiring', { params: { days } }),
};

// Checklists
export const checklistsApi = {
  list: () =>
    api.get<Checklist[]>('/checklists'),
  
  get: (id: string) =>
    api.get<Checklist>(`/checklists/${id}`),
  
  create: (data: CreateChecklistDto) =>
    api.post<Checklist>('/checklists', data),
  
  update: (id: string, data: CreateChecklistDto) =>
    api.patch<Checklist>(`/checklists/${id}`, data),
  
  delete: (id: string) =>
    api.delete(`/checklists/${id}`),
  
  assignToClient: (clientId: string, data: AssignChecklistDto) =>
    api.post(`/checklists/assign/${clientId}`, data),
  
  unassignFromClient: (clientId: string, checklistId: string) =>
    api.delete(`/checklists/assign/${clientId}/${checklistId}`),
  
  getClientChecklists: (clientId: string) =>
    api.get<ClientChecklist[]>(`/checklists/client/${clientId}`),

  downloadZip: (clientId: string, checklistId: string) =>
    api.get(`/checklists/client/${clientId}/${checklistId}/download-zip`, {
      responseType: 'blob',
    }),
};

// Checklist Templates
export const templatesApi = {
  list: () =>
    api.get<TemplatesByCategory>('/checklist-templates'),
  
  get: (id: string) =>
    api.get<ChecklistTemplate>(`/checklist-templates/${id}`),
  
  create: (data: CreateTemplateDto) =>
    api.post<ChecklistTemplate>('/checklist-templates', data),
  
  update: (id: string, data: UpdateTemplateDto) =>
    api.patch<ChecklistTemplate>(`/checklist-templates/${id}`, data),
  
  delete: (id: string) =>
    api.delete(`/checklist-templates/${id}`),
  
  copy: (id: string) =>
    api.post<ChecklistTemplate>(`/checklist-templates/${id}/copy`),
  
  assignToClient: (templateId: string, clientId: string, data?: AssignTemplateDto) =>
    api.post<ClientChecklist>(`/checklist-templates/${templateId}/assign/${clientId}`, data || {}),
};

// Deadlines
export const deadlinesApi = {
  list: () =>
    api.get<Deadline[]>('/deadlines'),
  
  upcoming: (days?: number) =>
    api.get<Deadline[]>('/deadlines/upcoming', { params: { days } }),
  
  get: (id: string) =>
    api.get<Deadline>(`/deadlines/${id}`),
  
  create: (data: CreateDeadlineDto) =>
    api.post<Deadline>('/deadlines', data),
  
  update: (id: string, data: UpdateDeadlineDto) =>
    api.patch<Deadline>(`/deadlines/${id}`, data),
  
  delete: (id: string) =>
    api.delete(`/deadlines/${id}`),
  
  assignToClient: (clientId: string, data: AssignDeadlineDto) =>
    api.post(`/deadlines/assign/${clientId}`, data),
  
  markComplete: (clientId: string, deadlineId: string) =>
    api.patch(`/deadlines/complete/${clientId}/${deadlineId}`),

  linkToChecklist: (clientDeadlineId: string, clientChecklistId: string) =>
    api.patch<ClientDeadline>(`/deadlines/link/${clientDeadlineId}`, { clientChecklistId }),

  autoCreateFromChecklist: (clientChecklistId: string) =>
    api.post<ClientDeadline>(`/deadlines/auto-create/${clientChecklistId}`),
};

// Portal (public)
export const portalApi = {
  getConfig: (token: string) =>
    api.get<PortalConfig>(`/portal/${token}`),
  
  upload: (token: string, data: FormData) =>
    api.post(`/portal/${token}/upload`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  download: (token: string, docId: string) =>
    api.get(`/portal/${token}/documents/${docId}/download`, {
      responseType: 'blob',
    }),

  deleteDocument: (token: string, docId: string) =>
    api.delete(`/portal/${token}/documents/${docId}`),
};

// Subscription
export const subscriptionApi = {
  getUsage: () =>
    api.get<SubscriptionUsage>('/subscription/usage'),
  
  getPlans: () =>
    api.get<PlanInfo[]>('/subscription/plans'),
  
  upgradePlan: (plan: string) =>
    api.post(`/subscription/upgrade/${plan}`),
};

// Stripe
export const stripeApi = {
  createCheckoutSession: (plan: string, interval: 'month' | 'year') =>
    api.post<{ url: string }>('/stripe/checkout-session', { plan, interval }),

  updateSubscription: (plan: string, interval: 'month' | 'year') =>
    api.post<{ plan: string; status: string }>('/stripe/update-subscription', { plan, interval }),

  createPortalSession: () =>
    api.post<{ url: string }>('/stripe/portal-session'),

  verifyCheckoutSession: (sessionId: string) =>
    api.post<{ plan: string; status: string }>('/stripe/verify-session', { sessionId }),
};

// Team
export const teamApi = {
  listMembers: () =>
    api.get<TeamListResponse>('/team/members'),

  inviteMember: (email: string) =>
    api.post<{ message: string }>('/team/invite', { email }),

  removeMember: (userId: string) =>
    api.delete<{ message: string }>(`/team/members/${userId}`),

  cancelInvite: (inviteId: string) =>
    api.delete<{ message: string }>(`/team/invites/${inviteId}`),
};

// Onboarding
export const onboardingApi = {
  completeStep: (step: string) =>
    api.patch<{ success: boolean }>('/onboarding/complete-step', { step }),

  dismiss: () =>
    api.patch<{ success: boolean; onboardingCompletedAt: string }>('/onboarding/dismiss'),
};

// Document Validity Configuration
export const documentValidityApi = {
  list: () =>
    api.get<DocumentTypeValidity[]>('/document-validity'),

  upsert: (documentType: string, data: { validityDays?: number | null; validityMode?: ValidityMode }) =>
    api.put<DocumentTypeValidity>(`/document-validity/${documentType}`, data),

  deleteOverride: (documentType: string) =>
    api.delete(`/document-validity/${documentType}`),
};

// Notification Rules
export const notificationRulesApi = {
  list: () =>
    api.get<NotificationRule[]>('/notification-rules'),

  get: (id: string) =>
    api.get<NotificationRule>(`/notification-rules/${id}`),

  create: (data: CreateNotificationRuleDto) =>
    api.post<NotificationRule>('/notification-rules', data),

  update: (id: string, data: Partial<CreateNotificationRuleDto>) =>
    api.patch<NotificationRule>(`/notification-rules/${id}`, data),

  delete: (id: string) =>
    api.delete(`/notification-rules/${id}`),

  evaluate: () =>
    api.get('/notification-rules/evaluate'),
};

// Types
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'OWNER' | 'GESTOR';
  gestoriaId: string;
  gestoria: {
    id: string;
    name: string;
    logoUrl?: string | null;
    accountStatus?: 'ACTIVE' | 'BLOCKED';
    blockedAt?: string | null;
    subscriptionStatus?: 'ACTIVE' | 'TRIAL' | 'EXPIRED' | 'CANCELLED' | 'PAST_DUE';
    trialEndsAt?: string | null;
  };
  emailNotifications?: boolean;
  deadlineReminders?: boolean;
  documentUpdates?: boolean;
  gdprConsentAt?: string | null;
  onboardingCompletedAt?: string | null;
  onboarding?: {
    hasCreatedClient: boolean;
    hasAssignedChecklist: boolean;
    hasSharedPortalLink: boolean;
    hasReviewedDocument: boolean;
  };
}

export interface Client {
  id: string;
  name: string;
  email: string;
  phone?: string;
  identifier?: string;
  type: 'AUTONOMO' | 'EMPRESA' | 'PARTICULAR';
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  uploadToken: string;
  notes?: string;
  caseStatus?: string | null;
  caseStatusAt?: string | null;
  archivedAt?: string;
  holdedContactId?: string | null;
  quipuContactId?: string | null;
  facturadirectaContactId?: string | null;
  createdAt: string;
  _count?: {
    documents: number;
    clientChecklists: number;
  };
}

export interface CaseStatusHistoryRecord {
  id: string;
  clientId: string;
  status: string;
  note?: string;
  updatedBy?: string;
  updatedByName?: string;
  createdAt: string;
}

export interface CreateClientDto {
  name: string;
  email: string;
  phone?: string;
  identifier?: string;
  type?: 'AUTONOMO' | 'EMPRESA' | 'PARTICULAR';
  notes?: string;
}

export interface UpdateClientDto extends Partial<Omit<CreateClientDto, 'phone' | 'identifier' | 'notes'>> {
  status?: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  phone?: string | null;
  identifier?: string | null;
  notes?: string | null;
}

export interface ClientsQueryParams {
  search?: string;
  status?: string;
  type?: string;
  page?: number;
  limit?: number;
}

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedClientsResponse {
  data: Client[];
  meta: PaginationMeta;
}

export interface Document {
  id: string;
  clientId: string;
  type: string;
  period?: string;
  year?: number;
  fileName: string;
  displayFileName?: string;
  fileSize: number;
  mimeType?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
  uploadedBy: 'CLIENT' | 'STAFF';
  notes?: string;
  rejectionReason?: string;
  issuedAt?: string;
  expiresAt?: string;
  createdAt: string;
  reviewedAt?: string;
  client?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface DocumentFilters {
  clientId?: string;
  type?: string;
  status?: string;
  period?: string;
  year?: number;
}

export interface UpdateDocumentDto {
  type?: string;
  period?: string;
  year?: number;
  status?: string;
  notes?: string;
  rejectionReason?: string;
  issuedAt?: string;
  expiresAt?: string;
  validityModeOverride?: ValidityMode;
}

export interface Checklist {
  id: string;
  name: string;
  description?: string;
  items: ChecklistEntry[];
  isDefault: boolean;
}

export interface UpdateChecklistDto {
  name?: string;
  description?: string;
  items?: ChecklistEntry[];
}

export interface ChecklistItem {
  id?: string;
  type?: 'item';
  documentType: string;
  required: boolean;
  description?: string;
}

export interface SectionDivider {
  type: 'divider';
  label: string;
}

export type ChecklistEntry = ChecklistItem | SectionDivider;

export function isDivider(entry: ChecklistEntry): entry is SectionDivider {
  return entry.type === 'divider';
}

export function isChecklistItem(entry: ChecklistEntry): entry is ChecklistItem {
  return !entry.type || entry.type === 'item';
}

export function getDocumentItems(entries: ChecklistEntry[]): ChecklistItem[] {
  return entries.filter(isChecklistItem);
}

export interface CreateChecklistDto {
  name: string;
  description?: string;
  items: ChecklistEntry[];
}

export interface AssignChecklistDto {
  checklistId: string;
  dueDate?: string;
  notes?: string;
}

export interface ClientChecklist {
  id: string;
  checklistId: string;
  checklist: Checklist;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE';
  dueDate?: string;
  completedItems: string[];
  clientDeadlines?: ClientDeadline[];
}

export interface Deadline {
  id: string;
  name: string;
  description?: string;
  dueDate: string;
  type: 'TAX' | 'CUSTOM';
  modelo?: string;
  isDefault: boolean;
  isRecurring?: boolean;
}

export interface CreateDeadlineDto {
  name: string;
  description?: string;
  dueDate: string;
  type?: 'TAX' | 'CUSTOM';
  modelo?: string;
  isRecurring?: boolean;
}

export interface UpdateDeadlineDto {
  name?: string;
  description?: string;
  dueDate?: string;
  type?: 'TAX' | 'CUSTOM';
  modelo?: string;
  isRecurring?: boolean;
}

export interface AssignDeadlineDto {
  deadlineId: string;
  notes?: string;
  clientChecklistId?: string;
}

export interface PortalConfig {
  clientName: string;
  gestoriaName: string;
  logoUrl?: string | null;
  checklists: PortalChecklist[];
  neededDocuments: string[]; // backwards compatibility
  otrosSubTypes: { description: string }[];
  existingDocuments: {
    id: string;
    type: string;
    fileName: string;
    status: string;
    period?: string;
    year?: number;
    createdAt: string;
    rejectionReason?: string;
    notes?: string;
    mimeType?: string;
  }[];
  deliverableDocuments: {
    id: string;
    type: string;
    fileName: string;
    status: string;
    period?: string;
    year?: number;
    createdAt: string;
    notes?: string;
    mimeType?: string;
  }[];
  documentTypes: {
    value: string;
    label: string;
  }[];
  caseStatus?: string | null;
  caseStatusUpdatedAt?: string | null;
  caseStatusHistory?: {
    id: string;
    status: string;
    createdAt: string;
  }[];
}

export interface PortalChecklist {
  id: string;
  name: string;
  description?: string;
  progress: number;
  dueDate?: string;
  items: ({
    id?: string;
    type?: 'item';
    documentType: string;
    required: boolean;
    description?: string;
    completed: boolean;
    status?: 'not_uploaded' | 'pending_review' | 'approved' | 'rejected';
    rejectionReasons?: string[];
  } | {
    type: 'divider';
    label: string;
  })[];
}

export interface ChecklistTemplate {
  id: string;
  gestoriaId: string | null;
  code: string;
  name: string;
  nameEs: string;
  nameRu?: string;
  description?: string;
  category: TemplateCategory;
  items: ChecklistEntry[];
}

export type TemplateCategory = 'EXTRANJERIA' | 'AUTONOMO' | 'TRAFICO' | 'HACIENDA' | 'FAMILIA' | 'LEGAL' | 'OTROS';

export type TemplatesByCategory = Record<TemplateCategory, ChecklistTemplate[]>;

export interface CreateTemplateDto {
  code: string;
  name: string;
  nameEs: string;
  nameRu?: string;
  description?: string;
  category: TemplateCategory;
  items: ChecklistEntry[];
}

export interface UpdateTemplateDto {
  name?: string;
  nameEs?: string;
  nameRu?: string;
  description?: string;
  category?: TemplateCategory;
  items?: ChecklistEntry[];
}

export interface AssignTemplateDto {
  dueDate?: string;
  notes?: string;
}

export interface SubscriptionUsage {
  plan: 'TRIAL' | 'SOLO' | 'TEAM' | 'PRO';
  subscriptionStatus: 'ACTIVE' | 'TRIAL' | 'EXPIRED' | 'CANCELLED' | 'PAST_DUE';
  trialEndsAt: string | null;
  trialDaysRemaining: number;
  usage: {
    clients: { current: number; max: number | null; percentage: number };
    users: { current: number; max: number; percentage: number };
    documents: { current: number; max: number; percentage: number };
    storage: {
      usedBytes: number;
      usedGB: number;
      maxGB: number;
      percentage: number;
    };
  };
}

export interface PlanInfo {
  plan: string;
  maxUsers: number;
  maxClients: number | null;
  storageGB: number;
  price: number;
  features: string[];
}

export interface LimitError {
  statusCode: number;
  message: string;
  limitType: 'clients' | 'users' | 'trial_expired';
  currentCount?: number;
  maxAllowed?: number;
  upgradeRequired: boolean;
}

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: 'OWNER' | 'GESTOR';
  joinedAt: string;
  lastLoginAt: string | null;
  status: 'ACTIVE';
}

export interface TeamPendingInvite {
  id: string;
  email: string;
  sentAt: string;
  expiresAt: string;
  status: 'PENDING';
}

export interface TeamListResponse {
  members: TeamMember[];
  pendingInvites: TeamPendingInvite[];
}

// Document Validity Types
export type ValidityMode = 'FIXED' | 'PERMANENT' | 'VARIABLE' | 'NOT_APPLICABLE';

export interface DocumentTypeValidity {
  documentType: string;
  validityDays: number | null;
  validityMode: ValidityMode;
  isOverridden: boolean;
  systemDefault: number | null;
  systemDefaultMode: ValidityMode;
}

// Client Deadline (assigned deadline with checklist link)
export interface ClientDeadline {
  id: string;
  clientId: string;
  deadlineId: string;
  deadline?: Deadline;
  status: 'PENDING' | 'COMPLETED';
  completedAt?: string;
  notes?: string;
  clientChecklistId?: string;
  clientChecklist?: ClientChecklist;
}

// Notification Rule Types
export type NotificationRuleType = 'DOCUMENT_EXPIRY' | 'CHECKLIST_INCOMPLETE' | 'DEADLINE_APPROACHING';
export type NotificationChannel = 'EMAIL' | 'IN_APP' | 'BOTH';

export interface NotificationRule {
  id: string;
  gestoriaId: string;
  name: string;
  type: NotificationRuleType;
  triggerDaysBefore: number;
  channel: NotificationChannel;
  isActive: boolean;
  templateKey?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNotificationRuleDto {
  name: string;
  type: NotificationRuleType;
  triggerDaysBefore: number;
  channel: NotificationChannel;
  isActive?: boolean;
  templateKey?: string;
}

export default api;
